// src/pages/PrivacyPolicy.jsx
import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => (
  <div className="privacy-container">
    <h1>Privacy Policy</h1>
    <p><strong>Effective Date:</strong> January 17, 2026</p>
    
    <p>
      This Privacy Policy explains how BoardWatch LLC ("BoardWatch," "we," "our," or "us")
      collects, uses, and safeguards information when you use our Enterprise Software platform 
      and related services (the "Service"). 
    </p>
    
    <p>
        Please note: If you are using BoardWatch as an authorized user of a school district, 
        organization, or company (the "Customer"), your use of the Service is also subject 
        to that Customer’s policies.
    </p>

    <section>
      <h2>1. Information We Collect</h2>
      <p>
        Unlike our public-facing pages, our Enterprise Service requires specific data to function 
        securely and effectively. We collect the following types of information:
      </p>
      
      <h3>A. Account Information</h3>
      <ul>
        <li><strong>Identity Data:</strong> Name, email address, job title, and login credentials (managed via secure authentication providers).</li>
        <li><strong>Organization Data:</strong> Information about the Customer you represent, including billing address and subscription status.</li>
      </ul>

      <h3>B. Usage & System Data</h3>
      <ul>
        <li><strong>Log Data:</strong> Internet Protocol (IP) address, browser type, time zone settings, and access times.</li>
        <li><strong>Activity:</strong> Metadata regarding how you interact with the dashboard, meeting transcripts, and voting tools to ensure system performance.</li>
      </ul>

      <h3>C. Customer Data</h3>
      <ul>
        <li>Data uploaded, imported, or generated by the Customer within the Service (e.g., meeting recordings, internal documents, votes). We process this data strictly on behalf of the Customer.</li>
      </ul>
    </section>

    <section>
      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect for the following business purposes:</p>
      <ul>
        <li><strong>To Provide the Service:</strong> Authenticating users, managing sessions, and processing meeting data.</li>
        <li><strong>Security & Compliance:</strong> Monitoring for fraudulent activity, enforcing access controls, and maintaining audit logs.</li>
        <li><strong>Support & Communication:</strong> Sending administrative notices, system updates, and responding to support tickets.</li>
        <li><strong>Improvement:</strong> analyzing aggregated, anonymized usage patterns to improve platform performance.</li>
      </ul>
    </section>

    <section>
      <h2>3. Cookies and Tracking Technologies</h2>
      <p>
        To provide a secure Enterprise environment, we utilize <strong>Essential Cookies</strong> and local storage. 
      </p>
      <ul>
        <li><strong>Authentication:</strong> Cookies help us verify your account and keep you logged in as you navigate the platform.</li>
        <li><strong>Security:</strong> We use cookies to detect unauthorized efforts to access account data.</li>
      </ul>
      <p>
        <em>Note: We do not use advertising cookies or sell your browsing data to third parties.</em>
      </p>
    </section>

    <section>
      <h2>4. Data Sharing and Disclosure</h2>
      <p>We do not sell your personal data. We disclose information only in the following circumstances:</p>
      <ul>
        <li><strong>Service Providers:</strong> We entrust data to third-party vendors who assist with hosting (e.g., AWS), authentication, and email delivery. These vendors are bound by strict confidentiality obligations.</li>
        <li><strong>The Customer:</strong> Administrators within your organization may have access to your usage logs and account activity.</li>
        <li><strong>Legal Requirements:</strong> If required by law, subpoena, or to protect the rights and safety of BoardWatch or others.</li>
      </ul>
    </section>

    <section>
      <h2>5. Data Security & Retention</h2>
      <p>
        We employ enterprise-grade security measures, including encryption in transit (TLS) and at rest, to protect your data. 
      </p>
      <p>
        <strong>Retention:</strong> We retain Customer Data for the duration of the Customer’s subscription agreement. Upon termination, data is deleted or returned in accordance with the service agreement, unless a longer retention period is required by law.
      </p>
    </section>

    <section>
      <h2>6. Your Rights</h2>
      <p>
        Depending on your location, you may have rights regarding your personal data, including accessing, correcting, or deleting your data.
      </p>
      <p>
        <strong>For Enterprise Users:</strong> Because BoardWatch processes data on behalf of your organization (the "Data Controller"), please direct privacy requests (such as deletion requests) to your organization’s administrator first. We will assist the Customer in fulfilling these requests.
      </p>
      <p>
         You may also contact us specifically regarding GDPR or CCPA inquiries at <a href="mailto:boardwatch.ai@gmail.com">boardwatch.ai@gmail.com</a>.
      </p>
    </section>

    <section>
      <h2>7. Updates to This Policy</h2>
      <p>
        As our Enterprise software evolves, we may update this policy. Significant changes will be communicated via email or an in-app notification.
      </p>
    </section>

    <section>
      <h2>Contact Us</h2>
      <p>
        If you have questions about this Enterprise Privacy Policy, please email us at <a href="mailto:boardwatch.ai@gmail.com">boardwatch.ai@gmail.com</a>.
      </p>
    </section>
  </div>
);

export default PrivacyPolicy;